import { Component, HostListener, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { timer, Subscription } from 'rxjs';
import { filter, share } from 'rxjs/operators';
import { FaConfig, FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { KeyboardShortcutsHelpComponent, KeyboardShortcutsModule } from 'ng-keyboard-shortcuts';
import { setTheme } from 'ngx-bootstrap/utils';
import { AuthService, BetaFeaturesService, CurrentTenantRegister, CurrentUserService, FieldTokenConnectorService, HistoryService, KeyboardShortcutService, NotificationService, RefreshTokenService, SessionService, SessionStore, WhitelabellingService } from '@parashift/shared/services';
import { ModalService } from '@parashift/shared/components';
import { betaFeaturesTranslations } from '@parashift/shared/constants';
import { FieldTokenConnectorComponent, OnboardingWizardService } from '@parashift/shared/features';
import { TreeShakingBusterService } from './services/tree-shaking-buster.service';
import { icons } from './icons.library';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [
    AsyncPipe,
    FieldTokenConnectorComponent,
    KeyboardShortcutsModule,
    RouterOutlet
]
})
export class AppComponent implements OnInit, OnDestroy {

  shortcutService = inject(KeyboardShortcutService);
  fieldTokenConnectorService = inject(FieldTokenConnectorService);

  private router = inject(Router);
  private notificationService = inject(NotificationService);
  private titleService = inject(Title);
  private authService = inject(AuthService);
  private sessionService = inject(SessionService);
  private modalService = inject(ModalService);
  private whitelabellingService = inject(WhitelabellingService);
  private historyService = inject(HistoryService);
  private currentUserService = inject(CurrentUserService);
  private refreshTokenService = inject(RefreshTokenService);
  private onboardingWizardService = inject(OnboardingWizardService);
  private treeShakingBusterService = inject(TreeShakingBusterService);
  private betaFeaturesService = inject(BetaFeaturesService);
  private sessionStore = inject(SessionStore);
  private currentTenantRegister = inject(CurrentTenantRegister);
  private library = inject(FaIconLibrary);
  private faConfig = inject(FaConfig);

  @ViewChild(KeyboardShortcutsHelpComponent, { static: true }) private keyboardHelp: KeyboardShortcutsHelpComponent;

  userSettingsInterval: Subscription;
  sessionTokenRefreshInterval: Subscription;
  globalTokenRefreshInterval: Subscription;
  redirectNotificationSubscription: Subscription;
  titleSubscription: Subscription;
  urlSubscription: Subscription;
  worker!: any;

  /**
   * Initialize singleton services at the earliest possible point
   * sessionService: Must be init here in order to get a current session after reload
   * authService: Checks if session is valid or not and redirects properly
   * historyService: Must be init here in order to record the requested url before redirects get fired
   */
  constructor() {
    setTheme('bs5');
    this.faConfig.autoAddCss = false;
    this.library.addIcons(...icons as IconDefinition[]);
    this.treeShakingBusterService.init();
    this.listenToBetaFeatureInit();
    this.initRefreshTokenInterval();
    this.initUserSettingsInterval();
    this.listenToRedirects();
    // FD: TODO: Leave it here for now for header component refactoring. If not needed remove it.
    // this.urlSubscription = this.historyService.currentUrl$.subscribe(url => {
    //   this.setTitle(url);
    // });
  }

  async ngOnInit() {
    this.listenToTenantTitle();
    this.shortcutService.setKeyboardHelp(this.keyboardHelp);
    this.onboardingWizardService.listenToRoutingChanges();
    this.whitelabellingService.init();
    this.sessionStore.init();
  }

  ngOnDestroy() {
    this.userSettingsInterval.unsubscribe();
    this.sessionTokenRefreshInterval.unsubscribe();
    this.globalTokenRefreshInterval.unsubscribe();
    this.redirectNotificationSubscription.unsubscribe();
    this.titleSubscription.unsubscribe();
    this.whitelabellingService.destroyListener();
    this.worker.close();
  }

  // A bfcache (back/forward) restore reuses the frozen page without re-running APP_INITIALIZER, so the
  // post-logout auth check never fires and the stale page is shown after logout. On restore, if the
  // persisted global token is gone/expired, hard-reload so bootstrap -> handleAuthentication -> login()
  // runs. Reads persisted (not in-memory) auth because the restored heap is a pre-logout snapshot;
  // a logged-in Back/Forward leaves the token intact and keeps bfcache (no reload).
  @HostListener('window:pageshow', ['$event'])
  onPageShow(event: PageTransitionEvent) {
    if (event.persisted && this.sessionService.global_token_expired_persisted) {
      location.reload();
    }
  }

  private listenToTenantTitle() {
    this.titleSubscription = this.currentTenantRegister.tenant$.subscribe(tenant => {
      const title = tenant?.name ? `Parashift - ${tenant.name}` : 'Parashift';
      this.titleService.setTitle(title);
    });
  }

  private initUserSettingsInterval() {
    const timerListener = timer(1000, 60000);
    this.userSettingsInterval = timerListener.subscribe(() => this.currentUserService.saveSettings());
  }

  private initRefreshTokenInterval() {
    // Derive the cadence from the token's own expiry (renew 60s before it lapses) rather than
    // hardcoding a period. Every mint carries the same TTL, so this stays correct across refreshes.
    const period = this.refreshPeriod();
    if (period <= 0) {
      return;
    }

    // One cadence, two independent refresh mechanisms — kept as separate subscriptions to make the
    // split explicit: the per-tab tenant-scoped session token, and the tenant-less global (identity)
    // token. share() so both ride the same timer rather than spinning up two.
    const refreshTick$ = timer(period, period).pipe(share());
    this.sessionTokenRefreshInterval = refreshTick$.subscribe(() => this.refreshTokenService.refresh());
    this.globalTokenRefreshInterval = refreshTick$.subscribe(() => this.authService.renewGlobalToken());
  }

  private refreshPeriod(): number {
    const REFRESH_BUFFER_SEC = 60;
    const nowSec = Date.now() / 1000;
    const expiresAtSec = Number(this.sessionService.expires_at);

    if (!Number.isFinite(expiresAtSec)) { return 0; } // no usable expiry — skip scheduling, don't arm a NaN timer

    return (expiresAtSec - nowSec - REFRESH_BUFFER_SEC) * 1000;
  }

  private listenToBetaFeatureInit() {
    this.betaFeaturesService.initBetaFeatures$.subscribe((state) => state && this.checkBetaFeatures());
  }

  private listenToRedirects() {
    this.redirectNotificationSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      filter((event: NavigationEnd) => {
        const original = event.url;
        const final = event.urlAfterRedirects;
        // No redirect happened
        if (original === final) { return false; }
        // Default routes just append a segment — final starts with original, not a renamed path
        if (final.startsWith(original)) { return false; }
        return true;
      })
    ).subscribe(() => {
      this.notificationService.warning({
        title: $localize `:@@redirect.path_changed.title:Outdated link`,
        description: $localize `:@@redirect.path_changed.description:You've been redirected automatically. Please update your bookmarks.`,
        config: { timeOut: 6000 }
      });
    });
  }

  private checkBetaFeatures() {
    setTimeout(() => {
      const { all_beta_features, beta_features, beta_features_default_values } = this.sessionService;
      const userNeverDecidedOn = feature => !beta_features || Object.keys(beta_features).length === 0 || beta_features[feature] === undefined;

      (all_beta_features || []).forEach(feature => {
        if (userNeverDecidedOn(feature)) {
          if (Object.prototype.hasOwnProperty.call(beta_features_default_values, feature)) {
            this.sessionService.setBetaFeature(feature, beta_features_default_values[feature]);
            this.currentUserService.saveSettings();
          } else {
            this.modalService
            .confirmation({
              initialState: {
                description: $localize `:@@app.beta_features_modal.description:Would you like to try out our new beta feature "${ betaFeaturesTranslations[feature] }:betaFeature:" <br><br><sup>(you can change this later in your Personal Settings, accessible via the bottom-left menu)</sup>`,
                title: $localize `:@@app.beta_features_modal.title:Beta Feature: ${ betaFeaturesTranslations[feature] }:betaFeature:`,
                backdrop: true,
                emitCancelEvent: true
              }
            })
            .subscribe((flag) => {
              this.sessionService.setBetaFeature(feature, flag as boolean);
              this.currentUserService.saveSettings();
            });
          }
        }
      });
    }, 2000);
  }
}
